import { ConversationPlatform } from "@redotech/redo-model/conversation";
import { Step as ModelStep } from "@redotech/redo-model/return-flow";
import {
  Condition,
  Condition as ModelCondition,
} from "@redotech/redo-model/return-flow/condition";
import { BlockLayout } from "@redotech/redo-web/flowchart";
import InfoIcon from "@redotech/redo-web/icon-old/info.svg";
import { produce } from "immer";
import * as React from "react";
import { ConditionType } from "../return-flow/condition";
import {
  AFTER_HOURS,
  AfterHoursState,
} from "../return-flow/condition-types/after-hours";
import {
  StepDownstream,
  StepId,
  StepType,
  StepTypeDetailsProps,
} from "../step";
import {
  ASSIGNMENT_TYPE,
  AssignmentTypeState,
} from "./conditions/assignment-type";
import {
  MESSAGE_BODY_MATCHES,
  MessageBodyMatchesState,
} from "./conditions/message-body-matches";
import {
  SENT_FROM_EMAIL,
  SentFromEmailState,
} from "./conditions/sent-from-email";
import { SUBJECT_LINE, SubjectLineState } from "./conditions/subject-line";
import { RuleTypeDetailsProps } from "./multiple-actions";
import { RECEIVED_AT_EMAIL, ReceivedAtEmailState } from "./received-at-email";
import { TICKET_CHANNEL, TicketChannelState } from "./ticket-channel";
import { TOPIC, TopicState } from "./topic";

export interface MultipleConditionsState {
  customTitle?: string;
  conditions: {
    conditionType: ConditionType<any, any> | undefined;
    conditionState?: any;
    customTitle?: string;
  }[];
  nextTrue: StepId | undefined;
  nextFalse: StepId | undefined;
}

export const MULTIPLE_CONDITIONS = (
  Details: React.ComponentType<StepTypeDetailsProps<MultipleConditionsState>>,
  DetailsForRuleConditions: DetailsComponentsForRuleConditions,
): StepType<
  MultipleConditionsState,
  { conditions: ModelStep.Condition[]; nextTrue?: number; nextFalse?: number }
> => ({
  Details(props: StepTypeDetailsProps<MultipleConditionsState>) {
    return React.createElement(Details, props);
  },
  customTitle(state) {
    return state.customTitle;
  },
  Icon: InfoIcon,
  description(state) {
    return state.conditions
      .map((condition) => {
        return condition.conditionType?.name || "One or more conditions";
      })
      .join(", ");
  },
  downstream(state) {
    const result: StepDownstream[] = [];
    if (state.nextFalse !== undefined) {
      result.push({ id: state.nextFalse, label: "False" });
    }
    if (state.nextTrue !== undefined) {
      result.push({ id: state.nextTrue, label: "True" });
    }
    return result;
  },
  maxDownstream() {
    return 2;
  },
  empty: { conditions: [], nextFalse: undefined, nextTrue: undefined },
  fromModel(model, id) {
    return {
      conditions: model.conditions.map((cond) => {
        if ((cond.condition.type as symbol) === Condition.AFTER_HOURS) {
          return {
            customTitle: cond.customTitle,
            conditionType: AFTER_HOURS(
              DetailsForRuleConditions.AfterHoursDetails,
            ),
            conditionState: AFTER_HOURS(
              DetailsForRuleConditions.AfterHoursDetails,
            ).fromModel(cond.condition as ModelCondition.AfterHours),
          };
        } else if (
          (cond.condition.type as symbol) === Condition.TICKET_CHANNEL
        ) {
          return {
            customTitle: cond.customTitle,
            conditionType: TICKET_CHANNEL(
              DetailsForRuleConditions.TicketChannelDetails,
            ),
            conditionState: TICKET_CHANNEL(
              DetailsForRuleConditions.TicketChannelDetails,
            ).fromModel(
              cond.condition as unknown as ModelCondition.TicketChannel,
            ),
          };
        } else if ((cond.condition.type as symbol) === Condition.SUBJECT_LINE) {
          return {
            customTitle: cond.customTitle,
            conditionType: SUBJECT_LINE(
              DetailsForRuleConditions.SubjectLineDetails,
            ),
            conditionState: SUBJECT_LINE(
              DetailsForRuleConditions.SubjectLineDetails,
            ).fromModel(
              cond.condition as unknown as ModelCondition.SubjectLine,
            ),
          };
        } else if (
          (cond.condition.type as symbol) === Condition.MESSAGE_BODY_MATCHES
        ) {
          return {
            customTitle: cond.customTitle,
            conditionType: MESSAGE_BODY_MATCHES(
              DetailsForRuleConditions.MessageBodyMatchesDetails,
            ),
            conditionState: MESSAGE_BODY_MATCHES(
              DetailsForRuleConditions.MessageBodyMatchesDetails,
            ).fromModel(
              cond.condition as unknown as ModelCondition.MessageBodyMatches,
            ),
          };
        } else if ((cond.condition.type as symbol) === Condition.TOPIC) {
          return {
            customTitle: cond.customTitle,
            conditionType: TOPIC(DetailsForRuleConditions.TopicDetails),
            conditionState: TOPIC(
              DetailsForRuleConditions.TopicDetails,
            ).fromModel(cond.condition as unknown as ModelCondition.Topic),
          };
        } else if (
          (cond.condition.type as symbol) === Condition.RECEIVED_AT_EMAIL
        ) {
          return {
            customTitle: cond.customTitle,
            conditionType: RECEIVED_AT_EMAIL(
              DetailsForRuleConditions.ReceivedAtEmailDetails,
            ),
            conditionState: RECEIVED_AT_EMAIL(
              DetailsForRuleConditions.ReceivedAtEmailDetails,
            ).fromModel(
              cond.condition as unknown as ModelCondition.ReceivedAtEmail,
            ),
          };
        } else if (
          (cond.condition.type as symbol) === Condition.SENT_FROM_EMAIL
        ) {
          return {
            customTitle: cond.customTitle,
            conditionType: SENT_FROM_EMAIL(
              DetailsForRuleConditions.SentFromEmailDetails,
            ),
            conditionState: SENT_FROM_EMAIL(
              DetailsForRuleConditions.SentFromEmailDetails,
            ).fromModel(
              cond.condition as unknown as ModelCondition.SentFromEmail,
            ),
          };
        } else if (
          (cond.condition.type as symbol) === Condition.ASSIGNMENT_TYPE
        ) {
          return {
            customTitle: cond.customTitle,
            conditionType: ASSIGNMENT_TYPE(
              DetailsForRuleConditions.AssignmentTypeDetails,
            ),
            conditionState: ASSIGNMENT_TYPE(
              DetailsForRuleConditions.AssignmentTypeDetails,
            ).fromModel(
              cond.condition as unknown as ModelCondition.AssignmentType,
            ),
          };
        } else {
          return {
            customTitle: cond.customTitle,
            conditionType: TICKET_CHANNEL(
              DetailsForRuleConditions.TicketChannelDetails,
            ),
            conditionState: TICKET_CHANNEL(
              DetailsForRuleConditions.TicketChannelDetails,
            ).fromModel({
              type: Condition.TICKET_CHANNEL,
              channels: [ConversationPlatform.INSTAGRAM],
            }),
          };
        }
      }),
      nextTrue: model.nextTrue?.toString(),
      nextFalse: model.nextFalse?.toString(),
    };
  },
  layout() {
    return BlockLayout.FULL;
  },
  stepDeleted(state, stepId) {
    return produce(state, (state) => {
      if (state.nextFalse === stepId) {
        state.nextFalse = undefined;
      }
      if (state.nextTrue === stepId) {
        state.nextTrue = undefined;
      }
    });
  },
  title: "Conditions",
  toModel(state, id) {
    return {
      type: ModelStep.MULTIPLE_CONDITIONS,
      conditions: state.conditions.map((cond) => {
        return {
          type: ModelStep.CONDITION,
          condition: cond.conditionType!.toModel(cond.conditionState),
        };
      }),
      nextTrue: state.nextTrue ? id(state.nextTrue) : undefined,
      nextFalse: state.nextFalse ? id(state.nextFalse!) : undefined,
    };
  },
  valid(state) {
    return (
      (state.nextTrue !== undefined || state.nextFalse !== undefined) &&
      state.conditions.length > 0
    );
  },
});

export type DetailsComponentsForRuleConditions = {
  AfterHoursDetails: React.ComponentType<RuleTypeDetailsProps<AfterHoursState>>;
  TicketChannelDetails: React.ComponentType<
    RuleTypeDetailsProps<TicketChannelState>
  >;
  SubjectLineDetails: React.ComponentType<
    RuleTypeDetailsProps<SubjectLineState>
  >;
  MessageBodyMatchesDetails: React.ComponentType<
    RuleTypeDetailsProps<MessageBodyMatchesState>
  >;
  TopicDetails: React.ComponentType<RuleTypeDetailsProps<TopicState>>;
  ReceivedAtEmailDetails: React.ComponentType<
    RuleTypeDetailsProps<ReceivedAtEmailState>
  >;
  SentFromEmailDetails: React.ComponentType<
    RuleTypeDetailsProps<SentFromEmailState>
  >;
  AssignmentTypeDetails: React.ComponentType<
    RuleTypeDetailsProps<AssignmentTypeState>
  >;
};
